import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import _get from "lodash/get";
import { Button, Checkbox, Modal, Row, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ImportFlexioPool from "../components/ImportFlexioPool";
import FlexioPoolHeader from "../components/Header";
import FlexioPoolTable from "../components/FlexioPoolTable";
import FilterFlexioPool from "../components/Filter";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import paginationFunc from "../../../../helper/hooks/pagination";
import profileApi from "modules/client/profile/store/api";
import { selectMetadata, updatePaginationPackages } from "../store/slice";
import "modules/admin/orders/styles/routes.scss";
import { refreshPackagesV2 } from "helper/getPackagesV2";
// required to hide the img tag for barcode generation
import "./FlexioPoolList.scss";
import flexioPoolApi from "../store/api";
import { setSuccessMessage } from "app/global/slice";
import { CheckCircleOutlined } from "@ant-design/icons";
import PoolStatus from "components/items/PoolStatus";
import _keys from "lodash/keys";
import { POOL_PACKAGE_STATUSES } from "helper/status";
import { separateStatusUnderlineLetter } from "modules/shared/PickupDropoff/helper";
import { FeatureFlagsContext } from "layouts/private/FeatureFlagsContext";
import { useHistory } from "react-router";
import { doExportCsv } from "helper/export";
import { buildFirstLevelFilterPoolExport } from "helper/util";

const breadCrumbs = [{ text: "FLEXIO POOL" }];

const FlexioPoolList = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [axiosPackageList, setAxiosPackageList] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState();
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [submiting, setSubmiting] = useState(false);
  const [newStatus, setNewStatus] = useState();
  const [packageArray, setPackageArray] = useState([]);
  const [selectAll, setSelectAll] = useState();
  const [batchStatusUpdate, setBatchStatusUpdate] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [profile, setProfile] = useState();
  const [exporting, setExporting] = useState(false);
  const [selectedPackageIds, setSelectedPackageIds] = useState(new Set());
  const [selectedPackagesCache, setSelectedPackagesCache] = useState(new Map());
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const dispatch = useDispatch();
  const metadata = useSelector(selectMetadata);
  const pagination = useMemo(
    () =>
      paginationFunc(
        { ...metadata, itemsPerPage: metadata?.itemsPerPage || 20 },
        "Packages",
        updatePaginationPackages,
        dispatch
      ),
    [metadata, dispatch]
  );
  const featureCtx = useContext(FeatureFlagsContext);
  const isPoolFeatureEnable = useMemo(() => featureCtx?.isPoolAvailable, [featureCtx]);
  const history = useHistory();

  useEffect(() => {
    if (isPoolFeatureEnable === false) {
      return history.push("/routes");
    }
  }, [isPoolFeatureEnable, history]);

  useEffect(() => {
    dispatch(getPackageTypeList());
    getProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = useMemo(() => metadata?.currentPage || 1, [metadata?.currentPage]);
  const pageSize = useMemo(() => metadata?.itemsPerPage || 20, [metadata?.itemsPerPage]);

  const getPackages = useCallback(() => {
    refreshPackagesV2(
      filterValues,
      page,
      pageSize,
      setAxiosPackageList,
      updatePaginationPackages,
      dispatch
    );
  }, [filterValues, page, pageSize, dispatch]);

  useEffect(() => {
    getPackages();
  }, [getPackages]);

  const getProfile = async () => {
    try {
      const profileRes = await profileApi.getProfile();
      if (!profileRes) return;
      setProfile({
        ...profileRes
      });
    } catch (err) {
      console.log(JSON.parse(err?.message || "{}"));
    }
  };

  const handleFilterChange = (newFilterValues) => {
    setFilterValues({
      ...newFilterValues,
      client_id: profile.clientId
    });
  };

  const handleReset = () => {
    setFilterValues({});
  };

  const handleUpdateStatus = async () => {
    try {
      setSubmiting(true);
      await flexioPoolApi.updatePackageStatus(selectedPackage?.id, { status: newStatus });
      dispatch(setSuccessMessage("Updated status successfully!"));
    } catch (err) {
      console.error("Error in handlePackageUpdate:", err);
    } finally {
      setSubmiting(false);
      setNewStatus(null);
      setShowUpdateStatus(false);
      getPackages();
    }
  };

  const handleBatchUpdate = async () => {
    for (const packageId of packageArray) {
      try {
        setSubmiting(true);
        await flexioPoolApi.updatePackageStatus(packageId, {
          status: newStatus
        });
      } catch (err) {
        console.error("Error in handleBatchUpdate:", err);
      }
    }
    dispatch(setSuccessMessage("Updated status successfully!"));
    setSubmiting(false);
    setShowUpdateStatus(false);
    setBatchStatusUpdate(false);
    setPackageArray([]);
    setSelectAll(false);
    getPackages();
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    const selectedPackages = selectAll ? [] : axiosPackageList?.map((pack) => pack.id);
    setPackageArray(selectedPackages);
  };

  const handlePackageArray = (pack) => {
    const tempArr = packageArray;
    /* Checks if the package already exists on the array to remove it if we uncheck the box */
    if (tempArr.includes(pack)) {
      const index = tempArr.indexOf(pack);
      tempArr.splice(index, 1);
      setPackageArray(tempArr);
    } else {
      packageArray.push(pack);
    }
  };
  const onExport = async () => {
    try {
      setExporting(true);
      await doExportCsv({
        path: "orderv2/export-client",
        name: `pool-orders-${new Date().getTime()}`,
        params: {
          filter: buildFirstLevelFilterPoolExport(
            filterValues?.pickupDateRange?.length > 0
              ? {
                  ...filterValues,
                  startPickupDate: filterValues.pickupDateRange[0],
                  endPickupDate: filterValues.pickupDateRange[1]
                }
              : {
                  ...filterValues
                }
          )
        }
      });
    } finally {
      setExporting(false);
    }
  };

  // Handle selection change for checkboxes
  const handleSelectionChange = (selectedKeys) => {
    // Update selected IDs - merge with existing selections from other pages
    const updatedSelectedIds = new Set(selectedPackageIds);

    // Get current page package IDs
    const currentPageIds = axiosPackageList.map((pkg) => pkg.id);

    // Remove all current page selections first
    currentPageIds.forEach((id) => updatedSelectedIds.delete(id));

    // Add new selections from current page
    selectedKeys.forEach((id) => updatedSelectedIds.add(id));

    setSelectedPackageIds(updatedSelectedIds);

    // Cache the selected packages data for cross-page access
    const newCache = new Map(selectedPackagesCache);

    // Add newly selected packages to cache
    selectedKeys.forEach((packageId) => {
      const packageData = axiosPackageList.find((pkg) => pkg.id === packageId);
      if (packageData) {
        newCache.set(packageId, packageData);
      }
    });

    // Remove unselected packages from current page
    currentPageIds.forEach((packageId) => {
      if (!selectedKeys.includes(packageId)) {
        newCache.delete(packageId);
      }
    });

    setSelectedPackagesCache(newCache);
  };

  // Get current page selected row keys
  const currentPageSelectedKeys = useMemo(() => {
    return axiosPackageList
      .filter((item) => selectedPackageIds.has(item.id))
      .map((item) => item.id);
  }, [axiosPackageList, selectedPackageIds]);

  // Toggle checkbox mode
  const toggleCheckboxMode = () => {
    setShowCheckboxes(!showCheckboxes);
    if (showCheckboxes) {
      // Clear selections when disabling checkbox mode
      setSelectedPackageIds(new Set());
      setSelectedPackagesCache(new Map());
    }
  };

  // Handle bulk print packing slips
  const handleBulkPrintPackingSlips = async () => {
    if (selectedPackageIds.size === 0) {
      dispatch(setSuccessMessage("Please select packages to print"));
      return;
    }

    try {
      setBulkPrinting(true);

      // Use cached package data for cross-page selections
      const allSelectedPackages = Array.from(selectedPackagesCache.values());

      if (allSelectedPackages.length > 0) {
        // Import the bulk print helper
        const { default: generatePdfPackingSlipPool } = await import(
          "helper/generatePdfPackingSlipPool"
        );

        // Create one PDF with all selected packages using the updated helper
        const selectedIds = Array.from(selectedPackageIds);
        const doc = await generatePdfPackingSlipPool(
          allSelectedPackages,
          null, // no single packageId
          selectedIds // array of package IDs for bulk printing
        );

        if (doc) {
          const filename = `Flexio-Pool-Bulk-${
            selectedPackageIds.size
          }-packages-${new Date().getTime()}.pdf`;
          doc.save(filename);
          dispatch(
            setSuccessMessage(
              `Printed ${selectedPackageIds.size} packing slips successfully!`
            )
          );

          // Clear selections after successful print
          setSelectedPackageIds(new Set());
          setSelectedPackagesCache(new Map());
        }
      } else {
        dispatch(setSuccessMessage("No package data available for printing"));
      }
    } catch (error) {
      console.error("Error in bulk print:", error);
      dispatch(setSuccessMessage("Error occurred while printing packing slips"));
    } finally {
      setBulkPrinting(false);
    }
  };

  return (
    <>
      {isPoolFeatureEnable && (
        <div>
          <FlexioPoolHeader
            breadCrumbs={breadCrumbs}
            showExport={true}
            onExport={onExport}
            exporting={exporting}
            showImport={true}
            setShowImport={setShowImport}
            setShowFilter={setShowFilter}
            showCheckboxes={showCheckboxes}
            onToggleCheckboxes={toggleCheckboxMode}
            selectedCount={selectedPackageIds.size}
            onBulkPrintPackingSlips={handleBulkPrintPackingSlips}
            bulkPrinting={bulkPrinting}
          />
          {showFilter && (
            <FilterFlexioPool
              setShowFilter={setShowFilter}
              onFilterChange={handleFilterChange}
              onReset={handleReset}
            />
          )}
          <div className="admin-content">
            <FlexioPoolTable
              dataSource={axiosPackageList}
              pagination={pagination}
              setSelectedPackage={setSelectedPackage}
              setShowUpdateStatus={setShowUpdateStatus}
              selectedRowKeys={currentPageSelectedKeys}
              onSelectionChange={handleSelectionChange}
              showCheckboxes={showCheckboxes}
            />
          </div>
          {showImport && (
            <Modal
              closable={false}
              open={true}
              title={"Import Flexio Pool Data"}
              className="no-padding-modal"
              onCancel={() => setShowImport(false)}
              footer={false}
              width={1000}
            >
              <ImportFlexioPool
                handleCloseModal={setShowImport}
                profile={profile}
                refreshRoutes={() => getPackages()}
                isAdmin
              />
            </Modal>
          )}
          {showUpdateStatus && (
            <Modal
              closable={false}
              open={true}
              title="Edit Package Status"
              width={"70%"}
              footer={
                <div className="buttons-modal">
                  <Button
                    size="large"
                    onClick={() => {
                      setShowUpdateStatus(false);
                      setNewStatus(null);
                      setSelectedPackage(null);
                      setPackageArray([]);
                      setSelectAll([]);
                    }}
                    loading={submiting}
                    disabled={submiting}
                  >
                    Cancel
                  </Button>

                  <Button
                    disabled={!newStatus || newStatus === selectedPackage?.status}
                    loading={submiting}
                    size="large"
                    type="primary"
                    onClick={!batchStatusUpdate ? handleUpdateStatus : handleBatchUpdate}
                  >
                    Update
                    <CheckCircleOutlined />
                  </Button>
                </div>
              }
            >
              <div className="d-flex flex-column gap-3 change-job-status-modal">
                {!batchStatusUpdate ? (
                  <Row className="gap-5">
                    <label>Current Status</label>
                    <div className="status">
                      <PoolStatus value={selectedPackage.status} />
                    </div>
                  </Row>
                ) : (
                  <>
                    <Row className="gap-5">
                      <label>Select Jobs</label>
                    </Row>
                    <Row className="gap-5">
                      <Checkbox onChange={handleSelectAll}>Select All</Checkbox>
                    </Row>
                    <div className="right-block jobs">
                      <FlexioPoolTable
                        dataSource={axiosPackageList}
                        pagination={pagination}
                        hiddenColumns={["Pickup Date", "Created At", "Last Updated At"]}
                        selectAll={selectAll}
                        handlePackageArray={handlePackageArray}
                      />
                    </div>
                  </>
                )}
                <Row className="flex-column gap-2">
                  <label>New Status</label>
                  <Select
                    listHeight={350}
                    size="large"
                    placeholder="Select status"
                    onChange={(status) => setNewStatus(status)}
                    style={{ width: "100%" }}
                    value={newStatus}
                  >
                    {_keys(POOL_PACKAGE_STATUSES).map((status) => {
                      if (status !== "cancelled") return;

                      return (
                        <Select.Option key={status} value={status}>
                          {separateStatusUnderlineLetter(status)}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Row>
              </div>
            </Modal>
          )}
        </div>
      )}
    </>
  );
};
export default FlexioPoolList;
